import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { AlertCircle, Calendar, User, MapPin, AlertTriangle, Wrench, FileText, Camera } from 'lucide-react';
import { useVessel } from '../../../../contexts/VesselContext';
import { useCrew } from '../../../../hooks/useCrew';
import { displayName } from '../crew/viewHelpers';
import { useSwipeBack, SwipeIndicator } from '../../../../hooks/useSwipeBack';
// FT-357: shell-level back handler — bottom bar owns Back. Header BackButton removed.
import { useSetBackHandler } from '../../../../contexts/NavigationBackContext';
import Dropdown from '../../../common/Dropdown';
import { useEquipmentRegister } from '../../../../hooks/domain/useEquipmentRegister';
import { useAuth } from '../../auth/AuthGate';
import PhotoAttachment from '../../../common/PhotoAttachment';
// FT-365 v3.1 hotfix: self-healing raw reader unwraps legacy double-JSON
// encoding left over from a brief FT-365 v3 exposure.
import { readCurrentVesselIdRaw } from '../../../../utils/readCurrentVesselId';

const RecordDefectView = ({ onBack, onSave, initialData = null, prefillData = null }) => {
  // FT-357: register onBack with the shell-level MobileBottomBar.
  useSetBackHandler(onBack);

  const { currentVesselId: contextVesselId, getCurrentVessel } = useVessel();
  const currentVesselId = contextVesselId || readCurrentVesselIdRaw();
  const currentVessel = getCurrentVessel();
  const { crew } = useCrew();
  const { containerRef, swipeProgress } = useSwipeBack(onBack);
  const { items: equipmentItems } = useEquipmentRegister();
  // FT-347: captured-by attribution for <PhotoAttachment>
  const { user: currentUser } = useAuth();
  const capturedBy = currentUser?.name || currentUser?.email || 'unknown';

  // FT-347: stable defect id from mount time so photos key correctly from the
  // first capture (before the form has been saved). On edit we reuse the
  // existing id; on create we generate one up-front and reuse it in handleSubmit.
  const [defectId] = useState(() => initialData?.id || `DEF-${Date.now()}`);

  // Create crew options for dropdowns
  const crewOptions = useMemo(() => {
    if (!crew) return [];
    return crew.map(member => ({
      value: displayName(member),
      label: displayName(member)
    }));
  }, [crew]);

  // Equipment options for dropdown
  const equipmentOptions = useMemo(() => {
    if (!equipmentItems) return [];
    return [
      { value: '', label: 'None - General defect' },  // Allow no equipment
      ...equipmentItems.map(item => ({
        value: item.id,
        label: `${item.name || item.label || item.description} - ${item.location || 'Unknown location'}`
      }))
    ];
  }, [equipmentItems]);

  // Risk level options
  const riskLevelOptions = [
    { value: 'Critical', label: 'Critical - Immediate action required' },
    { value: 'High', label: 'High - Urgent attention needed' },
    { value: 'Medium', label: 'Medium - Plan repair soon' },
    { value: 'Low', label: 'Low - Can wait for scheduled maintenance' }
  ];

  // Status options
  const statusOptions = [
    { value: 'Open', label: 'Open - Not yet addressed' },
    { value: 'In Progress', label: 'In Progress - Being worked on' },
    { value: 'Closed', label: 'Closed - Resolved' }
  ];

  const [formData, setFormData] = useState({
    date_reported: new Date().toISOString().split('T')[0],
    reported_by: '',
    description: '',
    location: '',
    risk_level: 'Medium',
    equipment_linked: '',          // Keep for display (backward compatible)
    equipment_id: '',              // NEW - Store equipment ID
    immediate_action_taken: '',
    fixed_immediately: false,
    assigned_to: '',
    status: 'Open',
    notes: '',
    photos: []                     // FT-347 - PhotoRef[]; may also carry legacy base64 strings on old records
  });

  const [errors, setErrors] = useState({});
  const [showCloseoutTips, setShowCloseoutTips] = useState(false);

  // Populate form when editing or prefilling
  useEffect(() => {
    if (initialData) {
      setFormData({
        date_reported: initialData.date_reported || new Date().toISOString().split('T')[0],
        reported_by: initialData.reported_by || '',
        description: initialData.description || '',
        location: initialData.location || '',
        risk_level: initialData.risk_level || 'Medium',
        equipment_linked: initialData.equipment_linked || '',
        equipment_id: initialData.equipment_id || '',      // NEW - Populate equipment ID
        immediate_action_taken: initialData.immediate_action_taken || '',
        fixed_immediately: initialData.fixed_immediately || false,
        assigned_to: initialData.assigned_to || '',
        status: initialData.status || 'Open',
        notes: initialData.notes || '',
        photos: Array.isArray(initialData.photos) ? initialData.photos : []  // FT-347
      });
    } else if (prefillData) {
      // Handle equipment prefill data - NOW STORES ID TOO
      setFormData(prev => ({
        ...prev,
        equipment_id: prefillData.equipmentId || '',        // NEW - Store ID
        equipment_linked: prefillData.equipmentName || '',  // Keep name for display
        location: prefillData.location || ''
      }));
    }
  }, [initialData, prefillData]);

  const handleInputChange = (field, value) => {
    // Single atomic setState with conditional logic to avoid race conditions
    setFormData(prev => {
      // Special handling for fixed_immediately checkbox - also update status
      if (field === 'fixed_immediately') {
        return {
          ...prev,
          fixed_immediately: value,
          status: value ? 'Closed' : 'Open'
        };
      }
      // Normal field update for all other fields
      return { ...prev, [field]: value };
    });

    // Clear error when user types
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Required fields
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }
    if (!formData.reported_by) {
      newErrors.reported_by = 'Reported by is required';
    }
    if (!formData.date_reported) {
      newErrors.date_reported = 'Date is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      // Scroll to first error
      const firstErrorField = Object.keys(errors)[0];
      const element = document.getElementById(firstErrorField);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.focus();
      }
      return;
    }
    
    // Create defect record
    // FT-347: reuse the mount-time defectId so any photos captured against it
    // stay keyed to the persisted record.
    const defectRecord = {
      ...formData,
      id: defectId,
      record_type: 'defect',
      created_at: initialData?.created_at || new Date().toISOString(),
      updated_at: new Date().toISOString(),
      history: initialData?.history || [{
        date: new Date().toISOString(),
        action: 'Created',
        by: formData.reported_by,
        notes: 'Initial defect report'
      }],
      // CRITICAL: Preserve existing audit trail for compliance
      auditLog: initialData?.auditLog || []
    };
    
    onSave(defectRecord);
  };

  return (
    <div className="min-h-screen bg-gray-50" ref={containerRef}>
      <SwipeIndicator progress={swipeProgress} />
      
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {/* FT-357: header BackButton removed — bottom bar owns Back. */}
              <div>
                <h1 className="text-lg sm:text-xl font-semibold text-gray-900">
                  {initialData ? 'Edit Defect' : 'Report Defect'}
                </h1>
                <p className="text-sm text-gray-500">
                  {currentVessel?.name || 'Defect Register'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Form Content */}
      {/* FT-83: pb-24 md:pb-0 prevents Save/Cancel buttons from being hidden behind mobile bottom nav */}
      <form onSubmit={handleSubmit}>
        <div className="max-w-3xl mx-auto p-6 pb-24 md:pb-6 space-y-6">
          
          {/* Basic Information Section */}
          <div className="rounded-2xl border border-gray-200 shadow-sm bg-white">
            <div className="bg-slate-700 px-5 py-3">
              <h2 className="text-base font-semibold text-white">Basic Information</h2>
            </div>

            <div className="px-6 py-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Date Reported */}
              <div>
                <label htmlFor="date_reported" className="block text-sm font-medium text-gray-700 mb-1">
                  <Calendar className="inline h-4 w-4 mr-1.5 -mt-0.5 text-cyan-600" aria-hidden="true" />
                  Date Reported <span className="text-red-500">*</span>
                </label>
                <input
                  id="date_reported"
                  type="date"
                  value={formData.date_reported}
                  onChange={(e) => handleInputChange('date_reported', e.target.value)}
                  className={`block w-full rounded-md border-0 py-2 px-3 text-gray-900 ring-1 ring-inset ${
                    errors.date_reported ? 'ring-red-300 focus:ring-red-600' : 'ring-gray-300 focus:ring-cyan-600'
                  } placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:text-sm`}
                />
                {errors.date_reported && (
                  <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {errors.date_reported}
                  </p>
                )}
              </div>

              {/* Reported By */}
              <div>
                <label htmlFor="reported_by" className="block text-sm font-medium text-gray-700 mb-1">
                  <User className="inline h-4 w-4 mr-1.5 -mt-0.5 text-cyan-600" aria-hidden="true" />
                  Reported By <span className="text-red-500">*</span>
                </label>
                <Dropdown
                  value={formData.reported_by}
                  onChange={(value) => handleInputChange('reported_by', value)}
                  options={crewOptions}
                  placeholder="Select crew member"
                  width="w-full"
                  error={errors.reported_by}
                />
                {errors.reported_by && (
                  <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {errors.reported_by}
                  </p>
                )}
              </div>
            </div>
            </div>
          </div>

          {/* Defect Details Section */}
          <div className="rounded-2xl border border-gray-200 shadow-sm bg-white">
            <div className="bg-slate-700 px-5 py-3">
              <h2 className="text-base font-semibold text-white">Defect Details</h2>
            </div>

            <div className="px-6 py-5">
            <div className="space-y-4">
              {/* Description */}
              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                  <FileText className="inline h-4 w-4 mr-1.5 -mt-0.5 text-cyan-600" aria-hidden="true" />
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  rows={3}
                  placeholder="Describe the defect in detail..."
                  className={`block w-full rounded-md border-0 py-2 px-3 text-gray-900 ring-1 ring-inset ${
                    errors.description ? 'ring-red-300 focus:ring-red-600' : 'ring-gray-300 focus:ring-cyan-600'
                  } placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:text-sm`}
                />
                {errors.description && (
                  <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {errors.description}
                  </p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  Be specific about what is damaged or not working properly
                </p>
              </div>

              {/* Location and Risk Level */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Location */}
                <div>
                  <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-1">
                    <MapPin className="inline h-4 w-4 mr-1.5 -mt-0.5 text-cyan-600" aria-hidden="true" />
                    Location
                  </label>
                  <input
                    id="location"
                    type="text"
                    value={formData.location}
                    onChange={(e) => handleInputChange('location', e.target.value)}
                    placeholder="e.g., Engine room, Bridge, Deck"
                    className="block w-full rounded-md border-0 py-2 px-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-cyan-600 sm:text-sm"
                  />
                </div>

                {/* Risk Level */}
                <div>
                  <label htmlFor="risk_level" className="block text-sm font-medium text-gray-700 mb-1">
                    <AlertTriangle className="inline h-4 w-4 mr-1.5 -mt-0.5 text-cyan-600" aria-hidden="true" />
                    Risk Level
                  </label>
                  <Dropdown
                    value={formData.risk_level}
                    onChange={(value) => handleInputChange('risk_level', value)}
                    options={riskLevelOptions}
                    width="w-full"
                  />
                </div>
              </div>

              {/* Equipment Linked - Dropdown from Equipment Register */}
              <div>
                <label htmlFor="equipment_id" className="block text-sm font-medium text-gray-700 mb-1">
                  <Wrench className="inline h-4 w-4 mr-1.5 -mt-0.5 text-cyan-600" aria-hidden="true" />
                  Equipment Linked
                </label>
                <Dropdown
                  value={formData.equipment_id}
                  onChange={(selectedId) => {
                    // Find equipment to get name for display
                    const selectedEquipment = equipmentItems?.find(item => item.id === selectedId);

                    // Update BOTH fields - ID for linking, name for display
                    setFormData(prev => ({
                      ...prev,
                      equipment_id: selectedId,
                      equipment_linked: selectedEquipment
                        ? `${selectedEquipment.name || selectedEquipment.label || selectedEquipment.description}`
                        : ''
                    }));

                    // Auto-fill location if equipment has one
                    if (selectedEquipment?.location && !formData.location) {
                      setFormData(prev => ({
                        ...prev,
                        location: selectedEquipment.location
                      }));
                    }
                  }}
                  options={equipmentOptions}
                  placeholder="Select equipment (optional)"
                  width="w-full"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Link to specific equipment from Equipment Register
                </p>
              </div>
            </div>
            </div>
          </div>

          {/* FT-347: Evidence Photos section — powered by <PhotoAttachment> (FT-343).
              Sits between Defect Details and Actions per DESIGN-BRIEF §6.3. */}
          {currentVesselId && (
            <div
              className="rounded-2xl border border-gray-200 shadow-sm bg-white"
              data-testid="defect-photos-section"
            >
              <div className="bg-slate-700 px-5 py-3">
                <div className="flex items-center gap-2">
                  <Camera className="h-4 w-4 text-white" aria-hidden="true" />
                  <h2 className="text-base font-semibold text-white">Evidence Photos</h2>
                </div>
              </div>
              <div className="px-6 py-5">
                <p className="mb-3 text-xs text-slate-500">
                  Capture photos of the defect. Photos sync across your devices.
                </p>
                <PhotoAttachment
                  recordType="defect"
                  recordId={defectId}
                  vesselId={currentVesselId}
                  capturedBy={capturedBy}
                  photos={formData.photos || []}
                  onChange={(nextPhotos) =>
                    setFormData((prev) => ({ ...prev, photos: nextPhotos }))
                  }
                  maxCount={20}
                  caption={true}
                  emptyLabel="No photos yet. Add photos to record defect evidence."
                  testId="defect-photos"
                />
              </div>
            </div>
          )}

          {/* Actions Section */}
          <div className="rounded-2xl border border-gray-200 shadow-sm bg-white">
            <div className="bg-slate-700 px-5 py-3">
              <h2 className="text-base font-semibold text-white">Actions</h2>
            </div>

            <div className="px-6 py-5">
            <div className="space-y-4">
              {/* Immediate Action Taken */}
              <div>
                <label htmlFor="immediate_action_taken" className="block text-sm font-medium text-gray-700 mb-1">
                  Immediate Action Taken
                </label>
                <textarea
                  id="immediate_action_taken"
                  value={formData.immediate_action_taken}
                  onChange={(e) => handleInputChange('immediate_action_taken', e.target.value)}
                  rows={2}
                  placeholder="Describe any immediate actions taken to address the defect..."
                  className="block w-full rounded-md border-0 py-2 px-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-cyan-600 sm:text-sm"
                />
              </div>

              {/* Fixed Immediately Checkbox */}
              <div className="flex items-start">
                <input
                  id="fixed_immediately"
                  type="checkbox"
                  checked={formData.fixed_immediately}
                  onChange={(e) => handleInputChange('fixed_immediately', e.target.checked)}
                  className="h-4 w-4 mt-1 text-cyan-600 rounded border-gray-300 focus:ring-cyan-600"
                />
                <div className="ml-3">
                  <label htmlFor="fixed_immediately" className="text-sm font-medium text-gray-700">
                    Fixed Immediately
                  </label>
                  <p className="text-xs text-gray-500">
                    Check if the defect was repaired right away
                  </p>
                </div>
              </div>

              {/* Assigned To and Status */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Assigned To */}
                <div>
                  <label htmlFor="assigned_to" className="block text-sm font-medium text-gray-700 mb-1">
                    <User className="inline h-4 w-4 mr-1.5 -mt-0.5 text-cyan-600" aria-hidden="true" />
                    Assigned To
                  </label>
                  <Dropdown
                    value={formData.assigned_to}
                    onChange={(value) => handleInputChange('assigned_to', value)}
                    options={crewOptions}
                    placeholder="Not assigned"
                    width="w-full"
                  />
                </div>

                {/* Status */}
                <div>
                  <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <Dropdown
                    value={formData.status}
                    onChange={(value) => handleInputChange('status', value)}
                    options={statusOptions}
                    width="w-full"
                    disabled={formData.fixed_immediately}
                  />
                </div>
              </div>
            </div>
            </div>
          </div>

          {/* Additional Notes Section */}
          <div className="rounded-2xl border border-gray-200 shadow-sm bg-white">
            <div className="bg-slate-700 px-5 py-3 flex items-center justify-between">
              <h2 className="text-base font-semibold text-white">Additional Notes</h2>
              <button
                type="button"
                onClick={() => setShowCloseoutTips(!showCloseoutTips)}
                className="text-sm text-white hover:text-slate-200"
              >
                {showCloseoutTips ? 'Hide' : 'Show'} Closeout Tips
              </button>
            </div>

            <div className="px-6 py-5">
            <div>
              {/* Notes */}
              <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                rows={2}
                placeholder="Any other relevant information..."
                className="block w-full rounded-md border-0 py-2 px-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-cyan-600 sm:text-sm"
              />
              
              {/* Closeout Tips */}
              {showCloseoutTips && (
                <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm font-medium text-amber-800 mb-2">Helpful Information for Closeouts:</p>
                  <ul className="text-xs text-amber-700 space-y-1">
                    <li>• <strong>Parts:</strong> Include part numbers, manufacturer, supplier details</li>
                    <li>• <strong>Contractor:</strong> Company name, contact person, phone number, invoice #</li>
                    <li>• <strong>Method:</strong> Step-by-step repair procedure for future reference</li>
                    <li>• <strong>Testing:</strong> How functionality was verified after repair</li>
                    <li>• <strong>Root Cause:</strong> Why the failure occurred (if known)</li>
                    <li>• <strong>Prevention:</strong> Steps taken to prevent recurrence</li>
                    <li>• <strong>Time:</strong> Labor hours spent on the repair</li>
                    <li>• <strong>Warranty:</strong> Any warranty information for parts or work</li>
                  </ul>
                </div>
              )}
            </div>
            </div>
          </div>

          {/* Compliance Note Section */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>WBC3 Compliance:</strong> This defect log complies with Section 8.1.4 & 8.3.4 requirements. 
              Once a defect is closed, it cannot be modified to maintain audit integrity.
            </p>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end gap-3 sticky bottom-0 bg-gray-50 py-4">
            <button
              type="button"
              onClick={onBack}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-cyan-600 border border-transparent rounded-md hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
            >
              {initialData ? 'Update Defect' : 'Report Defect'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default RecordDefectView;